<template>
  <div
    v-click-outside="closeDropdown"
    class="dropdown-select dropdown-select_custom"
    :class="{ 'is-active': isDropdownOpen }"
  >
    <btn
      type="button"
      class="dropdown-select__button button--primary button--block"
      :aria-expanded="isDropdownOpen"
      aria-haspopup="true"
      :label="selectedOption?.labelText || selectedOption?.label"
      tabindex="0"
      icon="icon-down"
      @click="toggleDropdown"
    />
    <transition name="fade">
      <ul
        v-show="isDropdownOpen"
        class="dropdown-select__list options-list"
        :class="{ 'is-open': isDropdownOpen }"
        role="listbox"
        :aria-hidden="!isDropdownOpen"
      >
        <li
          v-for="(option, index) in options"
          :id="`${option.value}-${option.id}`"
          :key="index"
          class="dropdown-select__list-item"
          :class="{ 'is-selected': option.value === selectedValue }"
          role="option"
          @click="selectOption(option.value)"
          v-html="option.label"
        />
      </ul>
    </transition>
  </div>
</template>

<script>

/**
 * Component: Dropdown Select
 * -----------------------------------------------------------------------------
 * - Custom dropdown select component.
 * - Global styles could be found in `src/styles/components/dropdown-select.scss`
 *
 * @param {Array}  [options] - Array of formatted options objects, each should contain `id`, `label` and `value`.
 * Optionally `labelText` could be added to display plain text instead of html.
 * @param {Number|String} [predefinedValue] - The value to be pre-selected (first option by default).
 */
import Btn from '~components/btn/btn'

export default {
  name: 'DropdownSelect',

  components: {
    Btn,
  },

  props: {
    options: {
      type: Array,
      default: () => [],
    },
    predefinedValue: {
      type: [Number, String],
      default: '',
    },
  },

  emits: ['on-option-selected'],

  data() {
    return {
      isDropdownOpen: false,
      selectedValue: this.predefinedValue || this.options[0]?.value,
    }
  },

  computed: {

    /**
     * Calculates chosen option based on selected value.
     * @returns {object}
     */
    selectedOption() {
      return this.findOptionByValue(this.selectedValue) || this.options[0]
    },
  },

  created() {

    /**
     * Emit the default selected option to the parent component.
     */
    this.$emit('on-option-selected', this.selectedValue)
  },

  methods: {

    /**
     * Toggles the visibility of the dropdown.
     */
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },

    /**
     * Closes the visibility of the dropdown.
     * - Used for outside clicks, when dropdown is open
     */
    closeDropdown() {
      this.isDropdownOpen = false
    },

    /**
     * Selects an option and closes the dropdown.
     *
     * @param {Object} value - The option value to be selected.
     * @emits `on-option-selected`
     */
    selectOption(value) {
      this.selectedValue = value
      this.isDropdownOpen = false

      this.$emit('on-option-selected', value)
    },

    /**
     * Finds an option in the options array by its value.
     *
     * @param {String} value - The value to search for.
     * @returns {object|boolean} The found option or `false` if not found.
     */
    findOptionByValue(value) {
      return this.options.find((option) => option.value === value) || false
    },
  },
}
</script>
